"use client";

import React from "react";

type PillItem<K extends string> = { key: K; label: string };

type BaseProps<K extends string> = {
  items: PillItem<K>[];
  className?: string;
};

type MultiProps<K extends string> = BaseProps<K> & {
  mode: "multi";
  selected: K[];
  onChange: (next: K[]) => void;
  allLabel?: string;         // 例: "All"
  showAllButton?: boolean;   // default true
};

type SingleProps<K extends string> = BaseProps<K> & {
  mode: "single";
  selected: K;
  onChange: (next: K) => void;
};

export function Pills<K extends string>(props: MultiProps<K> | SingleProps<K>) {
  const { items, className } = props;

  const isSelected = (k: K) => {
    return props.mode === "multi" ? props.selected.includes(k) : props.selected === k;
  };

  const handleClick = (k: K) => {
    if (props.mode === "single") {
      props.onChange(k);
      return;
    }

    const next = isSelected(k)
      ? props.selected.filter((x) => x !== k)
      : [...props.selected, k];

    props.onChange(next);
  };

  const clearAll = () => {
    if (props.mode === "multi") props.onChange([]);
  };

  const showAll =
    props.mode === "multi" ? (props.showAllButton ?? true) : false;

  return (
    <div
      className={[
        "flex flex-wrap gap-2 pb-4 pt-1 px-1",
        "md:flex-nowrap md:overflow-x-auto md:hide-scrollbar",
        className ?? "",
      ].join(" ")}
    >
      {showAll && props.mode === "multi" && (
        <button
          type="button"
          onClick={clearAll}
          data-testid="pill-すべて"
          className={[
            "shrink-0 px-4 py-2 rounded-full border text-sm",
            props.selected.length === 0
              ? "bg-white/15 border-white/30 text-white"
              : "bg-white/5 border-white/10 text-white/80",
          ].join(" ")}
        >
          {props.allLabel ?? "All"}
        </button>
      )}

      {items.map((p) => (
        <button
          key={p.key}
          type="button"
          onClick={() => handleClick(p.key)}
          data-testid={`pill-${p.key}`}
          className={[
            "shrink-0 px-4 py-2 rounded-full border text-sm",
            isSelected(p.key)
              ? "bg-white/15 border-white/30 text-white"
              : "bg-white/5 border-white/10 text-white/80",
          ].join(" ")}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}